// src/components/NotificationHistory/NotificationHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHistory,
    faFilter,
    faSearch,
    faEnvelope,
    faBell,
    faCheckCircle,
    faTimesCircle,
    faClock,
    faRedo,
    faEye,
    faCalendarAlt,
    faDownload,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { faTelegram } from '@fortawesome/free-brands-svg-icons';
import { useNotificationContext } from '../../contexts/NotificationContext';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const NotificationHistory = () => {
    const {
        selectedMarca,
        getHistory,
        retryNotification,
        getDeliveryStatus,
        loading
    } = useNotificationContext();

    const [notifications, setNotifications] = useState([]);
    const [filters, setFilters] = useState({
        channel: '',
        status: '',
        dateFrom: '',
        dateTo: '',
        search: ''
    });
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [stats, setStats] = useState({
        total: 0,
        sent: 0,
        failed: 0,
        pending: 0
    });

    const loadHistory = useCallback(async () => {
        try {
            const response = await getHistory(filters);
            setNotifications(response.notifications || []);
            setStats(response.stats || {
                total: 0,
                sent: 0,
                failed: 0,
                pending: 0
            });
        } catch (error) {
            console.error('Error cargando historial:', error);
        }
    }, [getHistory, filters]);

    useEffect(() => {
        if (selectedMarca) {
            loadHistory();
        }
    }, [selectedMarca, loadHistory]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleRetry = async (notificationId) => {
        try {
            await retryNotification(notificationId);
            loadHistory(); // Recargar historial
        } catch (error) {
            console.error('Error reenviando notificación:', error);
        }
    };

    const handleViewDetails = async (notification) => {
        try {
            const status = await getDeliveryStatus(notification.channel, notification.messageId);
            setSelectedNotification({
                ...notification,
                deliveryStatus: status
            });
        } catch (error) {
            console.error('Error obteniendo detalles:', error);
            setSelectedNotification(notification);
        }
    };

    const getChannelIcon = (channel) => {
        switch (channel) {
            case 'email': return faEnvelope;
            case 'telegram': return faTelegram;
            case 'push': return faBell;
            default: return faEnvelope;
        }
    };

    const getChannelColor = (channel) => {
        switch (channel) {
            case 'email': return 'text-blue-500';
            case 'telegram': return 'text-blue-400';
            case 'push': return 'text-orange-500';
            default: return 'text-gray-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent': return faCheckCircle;
            case 'failed': return faTimesCircle;
            case 'pending': return faClock;
            default: return faClock;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'sent': return 'text-green-500';
            case 'failed': return 'text-red-500';
            case 'pending': return 'text-yellow-500';
            default: return 'text-gray-500';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'sent': return 'Enviado';
            case 'failed': return 'Fallido';
            case 'pending': return 'Pendiente';
            default: return 'Desconocido';
        }
    };

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: es });
        } catch {
            return dateString;
        }
    };

    const exportHistory = () => {
        const csvContent = [
            ['Fecha', 'Canal', 'Destinatario', 'Estado', 'Mensaje'],
            ...notifications.map(notification => [
                formatDate(notification.createdAt),
                notification.channel,
                notification.recipient,
                getStatusText(notification.status),
                notification.message?.substring(0, 50) + '...'
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notificaciones_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                        <FontAwesomeIcon icon={faHistory} className="text-blue-500 text-2xl mr-3" />
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">
                                Historial de Notificaciones
                            </h2>
                            <p className="text-gray-600">
                                Revisa el estado de todas las notificaciones enviadas
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={exportHistory}
                        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center"
                    >
                        <FontAwesomeIcon icon={faDownload} className="mr-2" />
                        Exportar
                    </button>
                </div>

                {/* Estadísticas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                        <div className="text-sm text-blue-700">Total</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
                        <div className="text-sm text-green-700">Enviados</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
                        <div className="text-sm text-red-700">Fallidos</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
                        <div className="text-sm text-yellow-700">Pendientes</div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-4">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center text-gray-600 hover:text-gray-800"
                        >
                            <FontAwesomeIcon icon={faFilter} className="mr-2" />
                            Filtros
                        </button>
                        <button
                            onClick={loadHistory}
                            disabled={loading}
                            className="px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
                        >
                            {loading ? 'Cargando...' : 'Actualizar'}
                        </button>
                    </div>

                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Canal</label>
                                <select
                                    value={filters.channel}
                                    onChange={(e) => handleFilterChange('channel', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                    <option value="">Todos</option>
                                    <option value="email">Email</option>
                                    <option value="telegram">Telegram</option>
                                    <option value="push">Push</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Estado</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                    <option value="">Todos</option>
                                    <option value="sent">Enviado</option>
                                    <option value="failed">Fallido</option>
                                    <option value="pending">Pendiente</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Desde</label>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Hasta</label>
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-gray-600 mb-1">Buscar</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                        placeholder="Buscar..."
                                        className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-md text-sm"
                                    />
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Lista de notificaciones */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Canal
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Destinatario
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Mensaje
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {notifications.map((notification) => (
                                <tr key={notification._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                                            {formatDate(notification.createdAt)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon
                                                icon={getChannelIcon(notification.channel)}
                                                className={`mr-2 ${getChannelColor(notification.channel)}`}
                                            />
                                            <span className="text-sm text-gray-900 capitalize">
                                                {notification.channel}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {notification.recipient}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <FontAwesomeIcon
                                                icon={getStatusIcon(notification.status)}
                                                className={`mr-2 ${getStatusColor(notification.status)}`}
                                            />
                                            <span className={`text-sm ${getStatusColor(notification.status)}`}>
                                                {getStatusText(notification.status)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900">
                                        <div className="max-w-xs truncate">
                                            {notification.message}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex space-x-2">
                                            <button
                                                onClick={() => handleViewDetails(notification)}
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Ver detalles"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                            {notification.status === 'failed' && (
                                                <button
                                                    onClick={() => handleRetry(notification._id)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="Reenviar"
                                                >
                                                    <FontAwesomeIcon icon={faRedo} />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {notifications.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No se encontraron notificaciones
                    </div>
                )}
            </div>

            {/* Modal de detalles */}
            {selectedNotification && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">
                                    Detalles de la Notificación
                                </h3>
                                <button
                                    onClick={() => setSelectedNotification(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Fecha</label>
                                        <p className="text-sm text-gray-900">{formatDate(selectedNotification.createdAt)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Canal</label>
                                        <p className="text-sm text-gray-900 capitalize">{selectedNotification.channel}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Destinatario</label>
                                        <p className="text-sm text-gray-900">{selectedNotification.recipient}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Estado</label>
                                        <p className="text-sm text-gray-900">{getStatusText(selectedNotification.status)}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Mensaje</label>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedNotification.message}</p>
                                </div>

                                {selectedNotification.deliveryStatus && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Estado de Entrega</label>
                                        <pre className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md overflow-x-auto">
                                            {JSON.stringify(selectedNotification.deliveryStatus, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationHistory; 